import { Controller } from "stimulus"

// Collects what the current page says about itself into hidden form fields,
// and shows each piece as a removable chip so people see exactly what is sent.
// Pages describe themselves with marked elements:
//   <span data-feedback-context="model" data-feedback-group="model">Claude 4.5 Haiku</span>
// data-feedback-value overrides the element's text, data-feedback-label sets
// the chip text, and items that share a data-feedback-group share one chip.
export default class extends Controller {
  static targets = ["container", "chips", "fields"]
  static values = { scope: String }

  connect() {
    this.capture()
  }

  capture(event) {
    this.render(this.collect(event && event.detail && event.detail.trigger))
  }

  collect(trigger) {
    const items = [
      { key: "page", value: window.location.pathname, group: "page" },
      { key: "page_title", value: document.title, group: "page", label: document.title },
      { key: "screen", value: `${window.innerWidth}x${window.innerHeight}`, group: "device", label: this.chipsTarget.dataset.deviceLabel }
    ]
    document.querySelectorAll("[data-feedback-context]").forEach(el => {
      if (this.element.contains(el)) return

      const key = el.dataset.feedbackContext
      const value = (el.dataset.feedbackValue || el.textContent || "").trim()
      if (!key || !value) return

      const label = el.dataset.feedbackLabel || (el.dataset.feedbackValue ? null : value)
      items.push({ key, value, group: el.dataset.feedbackGroup || key, label })
    })
    if (trigger && trigger.dataset.feedbackError) {
      const error = trigger.dataset.feedbackError
      items.push({ key: "error", value: error, group: "error", label: error })
    }
    return items
  }

  render(items) {
    const scope = this.scopeValue || "feedback[context]"
    const labels = new Map()
    const seen = new Set()
    this.fieldsTarget.replaceChildren()
    this.chipsTarget.replaceChildren()

    items.forEach(item => {
      if (seen.has(item.key)) return
      seen.add(item.key)

      const input = document.createElement("input")
      input.type = "hidden"
      input.name = `${scope}[${item.key}]`
      input.value = item.value
      input.dataset.group = item.group
      this.fieldsTarget.appendChild(input)
      if (item.label) labels.set(item.group, item.label)
    })
    labels.forEach((label, group) => this.chipsTarget.appendChild(this.chip(group, label)))
    this.toggleContainer()
  }

  chip(group, label) {
    const chip = document.createElement("span")
    chip.className = "feedback-chip"
    const text = document.createElement("span")
    text.textContent = label.length > 48 ? `${label.slice(0, 47)}…` : label
    const remove = document.createElement("button")
    remove.type = "button"
    remove.className = "feedback-chip-remove"
    remove.setAttribute("aria-label", (this.chipsTarget.dataset.removeLabel || "Remove LABEL").replace("LABEL", label))
    remove.innerHTML = '<span class="far fa-xmark" aria-hidden="true"></span>'
    remove.addEventListener("click", () => this.remove(group, chip))
    chip.append(text, remove)
    return chip
  }

  remove(group, chip) {
    this.fieldsTarget.querySelectorAll(`input[data-group="${CSS.escape(group)}"]`).forEach(input => input.remove())
    if (group === "device") {
      // The browser is read from the request, so tell the server to skip it too.
      const optOut = document.createElement("input")
      optOut.type = "hidden"
      optOut.name = `${this.scopeValue || "feedback[context]"}[browser]`
      optOut.value = "none"
      this.fieldsTarget.appendChild(optOut)
    }
    chip.remove()
    this.toggleContainer()
  }

  toggleContainer() {
    this.containerTarget.hidden = this.chipsTarget.children.length === 0
  }
}
